import { Helmet } from "react-helmet";
import { useEffect, useState, useCallback } from "react";
import SwaggerUI from "swagger-ui-react";
import "swagger-ui-react/swagger-ui.css";
import {
    apiRequestLimits,
    getSubFees,
    getPartnerApiKeyMeta,
    generatePartnerApiKey,
    revokePartnerApiKey,
} from "../store/actions";
import { useDispatch, useSelector } from "react-redux";
import yaml from "js-yaml";
import useWindowSize from "../utils/useWindowSize";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faKey,
    faCopy,
    faSpinner,
    faTriangleExclamation,
    faRotate,
    faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "react-modal";
import { toast } from "react-toastify";
import DocsMarkdown from "../components/DocsMarkdown";

// Markdown docs next to the OpenAPI specs (public/docs). The partner's affiliate id
// replaces the sample id in the examples, like the spec injection below.
const DOCS = {
    Guides: "/docs/walletless-guides.md",
    Changelog: "/docs/walletless-changelog.md",
};
const fetchDoc = async (tab) => {
    const res = await fetch(DOCS[tab]);
    if (!res.ok) throw new Error(`Failed to load ${tab} (${res.status})`);
    const text = await res.text();
    const affiliateId = localStorage.getItem("affiliateId");
    return affiliateId ? text.replace(/"affiliate_id":\s*"[^"]*"/g, `"affiliate_id": "${affiliateId}"`) : text;
};

// The walletless tab shows UPDATED until the partner opens it once per docs version
// (bump together with info.version in public/walletless-swagger.yaml).
const WALLETLESS_DOCS_VERSION = "1.13.0";
const WALLETLESS_SEEN_KEY = "apSeenWalletlessDocs";

// Walletless OpenAPI spec with the partner's affiliateId injected into every example.
const loadWalletlessSpec = async () => {
    const res = await fetch("/walletless-swagger.yaml");
    const text = await res.text();
    const obj = yaml.load(text);
    const affiliateId = localStorage.getItem("affiliateId");
    try {
        if (affiliateId && obj && obj.paths) {
            const quoteLite = obj.paths["/quote-lite"]?.post?.requestBody?.content?.["application/json"];
            if (quoteLite) {
                if (!quoteLite.example) quoteLite.example = {};
                quoteLite.example.affiliateId = affiliateId;
            }
            const swapLite = obj.paths["/swap-lite"]?.post?.requestBody?.content?.["application/json"];
            if (swapLite) {
                if (!swapLite.example) swapLite.example = {};
                swapLite.example.affiliateId = affiliateId;
            }
            if (obj.components?.schemas) {
                const schemas = obj.components.schemas;
                ['QuoteLiteRequest', 'SwapLiteRequest'].forEach(name => {
                    const prop = schemas[name]?.properties?.affiliateId;
                    if (prop) prop.example = affiliateId;
                });
            }
            if (obj.info?.description) {
                obj.info.description = obj.info.description.replace(/"affiliate_id":\s*"[^"]*"/, `"affiliate_id": "${affiliateId}"`);
            }
        }
    } catch (e) {
        // If parsing/mutation fails, fall back to original
    }
    return obj;
};

const apiKeyModalStyles = {
    overlay: { backgroundColor: "rgba(6, 7, 7, 0.78)", zIndex: 2000 },
    content: {
        top: "50%",
        left: "50%",
        right: "auto",
        bottom: "auto",
        transform: "translate(-50%, -50%)",
        width: "90%",
        maxWidth: 560,
        padding: 0,
        background: "transparent",
        border: "none",
        overflow: "visible",
    },
};

const copyApiKey = (value) => {
    if (!value) return;
    navigator.clipboard
        .writeText(value)
        .then(() => toast.success("Copied to clipboard"))
        .catch(() => toast.error("Failed to copy"));
};

// API key management for the Partner CMS reporting endpoints (ppapi backend live).
// Session-hash auth, in-CMS only. The plaintext key is shown ONCE on generate.
const PartnerApiKeyPanel = () => {
    const dispatch = useDispatch();
    const [meta, setMeta] = useState(null); // { hasKey, prefix, lastFour, createdAt, lastUsedAt }
    const [loading, setLoading] = useState(true);
    const [working, setWorking] = useState(false);
    const [newKey, setNewKey] = useState(null); // show-once plaintext key

    const loadMeta = useCallback(() => {
        setLoading(true);
        dispatch(getPartnerApiKeyMeta())
            .then((res) => setMeta(res || { hasKey: false }))
            .catch((err) => {
                if (err?.response?.status === 450) {
                    toast.error("Your session expired. Please sign in again.");
                }
                setMeta({ hasKey: false });
            })
            .finally(() => setLoading(false));
    }, [dispatch]);

    useEffect(() => {
        loadMeta();
    }, [loadMeta]);

    const handleGenerate = () => {
        const isRegen = meta?.hasKey;
        if (isRegen && !window.confirm("Regenerate your API key? The current key will stop working immediately.")) {
            return;
        }
        setWorking(true);
        dispatch(generatePartnerApiKey())
            .then((res) => {
                if (res?.apiKey) {
                    setNewKey(res.apiKey);
                    toast.success(isRegen ? "API key regenerated." : "API key created.");
                }
                loadMeta();
            })
            .catch((err) => {
                if (err?.response?.status === 450) {
                    toast.error("Your session expired. Please sign in again.");
                } else {
                    toast.error("Failed to generate API key.");
                }
            })
            .finally(() => setWorking(false));
    };

    const handleRevoke = () => {
        if (!window.confirm("Revoke your API key? Any server using it will stop working.")) return;
        setWorking(true);
        dispatch(revokePartnerApiKey())
            .then(() => {
                toast.success("API key revoked.");
                loadMeta();
            })
            .catch((err) => {
                if (err?.response?.status === 450) {
                    toast.error("Your session expired. Please sign in again.");
                } else {
                    toast.error("Failed to revoke API key.");
                }
            })
            .finally(() => setWorking(false));
    };

    const closeNewKeyModal = () => setNewKey(null);

    const hasKey = Boolean(meta?.hasKey);

    return (
        <div className="panel" style={{ marginBottom: 20 }}>
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-center gap-3" style={{ minWidth: 0, flex: "1 1 320px" }}>
                    <div
                        style={{
                            width: 42,
                            height: 42,
                            borderRadius: 11,
                            background: "rgba(58,209,126,.12)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <FontAwesomeIcon icon={faKey} style={{ color: "#3ad17e", fontSize: 17 }} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <h3 className="pTitle" style={{ fontSize: 15 }}>
                            Partner API Key
                        </h3>
                        <p className="pSub" style={{ marginTop: 2 }}>
                            A permanent key for calling these reporting endpoints from your own
                            backend (server-to-server / cron), instead of the short-lived session
                            token used inside this dashboard.
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                    {loading ? (
                        <FontAwesomeIcon icon={faSpinner} spin style={{ color: "#3ad17e", fontSize: 18 }} />
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={handleGenerate}
                                disabled={working}
                                className="btnPrimary sm"
                            >
                                {working ? (
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                ) : (
                                    <FontAwesomeIcon icon={hasKey ? faRotate : faKey} />
                                )}
                                {hasKey ? "Regenerate" : "Generate key"}
                            </button>
                            {hasKey && (
                                <button
                                    type="button"
                                    onClick={handleRevoke}
                                    disabled={working}
                                    className="btnGhost sm danger"
                                    style={{ color: "#ff9d94", borderColor: "rgba(255,122,110,.35)" }}
                                >
                                    <FontAwesomeIcon icon={faTrashCan} /> Revoke
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>

            {!loading && (
                <div style={{ marginTop: 16 }}>
                    {hasKey ? (
                        <div
                            className="flex flex-wrap items-center gap-x-8 gap-y-2"
                            style={{
                                padding: "12px 16px",
                                background: "#0d0e0e",
                                borderRadius: 11,
                                border: "1px solid rgba(255,255,255,.08)",
                            }}
                        >
                            <div>
                                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: "#6e756f" }}>
                                    Key
                                </div>
                                <div className="cellMono" style={{ color: "#eef3f0", fontSize: 13.5 }}>
                                    {(meta.prefix || "isk_live_")}••••{meta.lastFour}
                                </div>
                            </div>
                            <div>
                                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: "#6e756f" }}>
                                    Created
                                </div>
                                <div style={{ fontSize: 13, color: "#cdd5cf" }}>
                                    {meta.createdAt || "—"}
                                </div>
                            </div>
                            <div>
                                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: "#6e756f" }}>
                                    Last used
                                </div>
                                <div style={{ fontSize: 13, color: "#cdd5cf" }}>
                                    {meta.lastUsedAt || "Never"}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <p style={{ fontSize: 13, color: "#7c847e" }}>
                            No API key yet. Generate one to call the reporting endpoints from your
                            own backend.
                        </p>
                    )}
                    <p className="fldHelp" style={{ marginTop: 12 }}>
                        Send the key in the header{" "}
                        <code>X-Partner-Api-Key: isk_live_…</code> on the reporting endpoints. It
                        does not expire — it stays valid until you regenerate or revoke it. Inside
                        this dashboard, requests keep using your session (<code>X-Partner-Hash</code>).
                    </p>
                </div>
            )}

            <Modal
                isOpen={Boolean(newKey)}
                onRequestClose={closeNewKeyModal}
                style={apiKeyModalStyles}
                ariaHideApp={false}
                contentLabel="Your new API key"
            >
                <div className="iswModal" style={{ maxWidth: "none", borderColor: "rgba(58,209,126,.4)" }}>
                    <div className="flex items-center gap-2" style={{ marginBottom: 12 }}>
                        <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: "#e6c06a" }} />
                        <h2 className="mTitle" style={{ margin: 0 }}>Save your API key now</h2>
                    </div>
                    <p className="mSub">
                        This is the only time the full key is shown. Store it somewhere safe — you
                        won't be able to see it again. If you lose it, regenerate a new one.
                    </p>
                    <div className="lwAddrBox" style={{ margin: 0 }}>
                        <span style={{ flex: "1 1 240px", wordBreak: "break-all", fontSize: 12.5 }}>
                            {newKey}
                        </span>
                        <div className="acts">
                            <button
                                type="button"
                                onClick={() => copyApiKey(newKey)}
                                className="btnGhost sm"
                            >
                                <FontAwesomeIcon icon={faCopy} /> Copy
                            </button>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={closeNewKeyModal}
                        className="btnPrimary block"
                        style={{ marginTop: 20 }}
                    >
                        I've saved it
                    </button>
                </div>
            </Modal>
        </div>
    );
};

const ApiReference = () => {
    const dispatch = useDispatch();
    const sized = useWindowSize();
    const isMobile = (sized?.width || 0) < 768;

    const tabs = [
        "Guides",
        "DEX Walletless Api",
        "Trade API",
        "Partners cms api",
        "Changelog",
    ];
    const [activeTab, setActiveTab] = useState("Guides");
    const [docs, setDocs] = useState({}); // tab -> markdown | { error }
    const [guidesQuery, setGuidesQuery] = useState("");
    const [walletlessSeen, setWalletlessSeen] = useState(() => localStorage.getItem(WALLETLESS_SEEN_KEY) === WALLETLESS_DOCS_VERSION);
    const openTab = (tab) => {
        setActiveTab(tab);
        if (tab === "DEX Walletless Api" && !walletlessSeen) {
            localStorage.setItem(WALLETLESS_SEEN_KEY, WALLETLESS_DOCS_VERSION);
            setWalletlessSeen(true);
        }
    };
    const [walletlessSpec, setWalletlessSpec] = useState(null);
    const [walletlessErr, setWalletlessErr] = useState(null);
    const [tradeSpec, setTradeSpec] = useState(null);
    const [tradeErr, setTradeErr] = useState(null);
    const [partnerCmsSpec, setPartnerCmsSpec] = useState(null);
    const [partnerCmsErr, setPartnerCmsErr] = useState(null);
    const [aiCopied, setAiCopied] = useState(false);
    const subFees = useSelector((state) => state.userReducer.subFees || []);
    const partnerName = useSelector(
        (state) =>
            state.userReducer.user?.response?.partnerPublicName ||
            state.userReducer.user?.response?.partnerName ||
            null
    );

    useEffect(() => {
        dispatch(apiRequestLimits());
    }, [dispatch]);

    // Guides / Changelog markdown, loaded once per tab.
    useEffect(() => {
        if (!DOCS[activeTab] || docs[activeTab]) return;
        fetchDoc(activeTab)
            .then((text) => setDocs((d) => ({ ...d, [activeTab]: text })))
            .catch((e) => setDocs((d) => ({ ...d, [activeTab]: { error: e?.message || "Failed to load" } })));
    }, [activeTab, docs]);

    // Pull the partner's sub-affiliates so we can show a real, active
    // X-Sub-Affiliate-Id in the docs example instead of a placeholder.
    useEffect(() => {
        dispatch(getSubFees()).catch(() => {});
    }, [dispatch]);

    // Inject an active sub-affiliate id into the walletless docs example header.
    useEffect(() => {
        if (!walletlessSpec) return;
        const active = subFees.find((sf) => Number(sf.active) === 1) || subFees[0];
        const subId = active?.subAffiliateId;
        if (!subId) return;
        const param = walletlessSpec.components?.parameters?.SubAffiliateIdHeader;
        if (param && param.example !== subId) {
            setWalletlessSpec({
                ...walletlessSpec,
                components: {
                    ...walletlessSpec.components,
                    parameters: {
                        ...walletlessSpec.components.parameters,
                        SubAffiliateIdHeader: { ...param, example: subId },
                    },
                },
            });
        }
    }, [walletlessSpec, subFees]);
    // Load and customize Walletless YAML to inject affiliateId in examples
    useEffect(() => {
        if (activeTab !== "DEX Walletless Api" || walletlessSpec) return;
        loadWalletlessSpec()
            .then(setWalletlessSpec)
            .catch((e) => {
                setWalletlessErr(e?.message || "Failed to load Walletless spec");
            });
    }, [activeTab, walletlessSpec]);

    // Load the Trade API YAML (perps + spot + stocks control plane) and
    // inject the partner's real affiliateId into every example.
    useEffect(() => {
        if (activeTab !== "Trade API" || tradeSpec) return;
        fetch("/trade-api-swagger.yaml")
            .then((res) => res.text())
            .then((text) => {
                const obj = yaml.load(text);
                const affiliateId = localStorage.getItem("affiliateId");
                try {
                    if (affiliateId && obj) {
                        // request-body examples (fills + closes)
                        ["/v2/trade/fills", "/v2/trade/closes"].forEach((p) => {
                            const media = obj.paths?.[p]?.post?.requestBody?.content?.["application/json"];
                            if (media?.example) media.example.affiliateId = affiliateId;
                        });
                        // config query-param example
                        const cfgParams = obj.paths?.["/v2/trade/config"]?.get?.parameters;
                        const affParam = Array.isArray(cfgParams) ? cfgParams.find((x) => x.name === "affiliateId") : null;
                        if (affParam) affParam.example = affiliateId;
                        // schema property examples
                        ["FillReport", "CloseReport"].forEach((name) => {
                            const prop = obj.components?.schemas?.[name]?.properties?.affiliateId;
                            if (prop) prop.example = affiliateId;
                        });
                    }
                } catch (e) {
                    // fall back to the raw spec
                }
                setTradeSpec(obj);
            })
            .catch((e) => {
                setTradeErr(e?.message || "Failed to load Trade API spec");
            });
    }, [activeTab, tradeSpec]);

    // Load Partners CMS API YAML (read-only partner reporting)
    useEffect(() => {
        if (activeTab !== "Partners cms api" || partnerCmsSpec) return;
        fetch("/partner-cms-swagger.yaml")
            .then((res) => res.text())
            .then((text) => {
                setPartnerCmsSpec(yaml.load(text));
            })
            .catch((e) => {
                setPartnerCmsErr(e?.message || "Failed to load Partners CMS API spec");
            });
    }, [activeTab, partnerCmsSpec]);

    // Replace the hardcoded 200-response example in the Partners CMS spec with a
    // live one built from the partner's real name and their actual sub-affiliates.
    useEffect(() => {
        if (!partnerCmsSpec) return;
        const mediaType =
            partnerCmsSpec.paths?.["/formData"]?.post?.responses?.["200"]?.content?.[
                "application/json"
            ];
        if (!mediaType) return;

        const now = new Date();
        const toDay = now.toISOString().slice(0, 10);
        const fromDay = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
            .toISOString()
            .slice(0, 10);

        // Illustrative (descending) figures attached to the partner's real subs.
        const sampleFees = [80.1, 30.25, 13.1];
        const sampleSwaps = [50, 22, 15];
        const subs = subFees.slice(0, 3);
        const bySubAffiliate = subs.length
            ? subs.map((sf, i) => ({
                  subAffiliateId: sf.subAffiliateId,
                  subLabel: sf.subLabel || null,
                  fees: sampleFees[i] ?? 0,
                  swaps: sampleSwaps[i] ?? 0,
              }))
            : [
                  {
                      subAffiliateId: "(no sub-affiliates yet)",
                      subLabel: null,
                      fees: 0,
                      swaps: 0,
                  },
              ];

        const totalsFees = Number(
            bySubAffiliate.reduce((acc, r) => acc + r.fees, 0).toFixed(2)
        );
        const totalsSwaps = bySubAffiliate.reduce((acc, r) => acc + r.swaps, 0);

        const example = {
            response: {
                partnerName: partnerName || "Your Partner",
                currency: "USD",
                range: {
                    from: `${fromDay}T00:00:00.000Z`,
                    to: `${toDay}T23:59:59.999Z`,
                },
                totals: {
                    subAffiliateFees: totalsFees,
                    swaps: totalsSwaps,
                },
                bySubAffiliate,
            },
        };

        if (JSON.stringify(mediaType.example) === JSON.stringify(example)) return;

        const next = JSON.parse(JSON.stringify(partnerCmsSpec));
        next.paths["/formData"].post.responses["200"].content[
            "application/json"
        ].example = example;
        setPartnerCmsSpec(next);
    }, [partnerCmsSpec, subFees, partnerName]);

    return (
        <>
            <Helmet>
                <title>API Reference | Instaswap Partners</title>
            </Helmet>
            <div>
                {/* Tabs Header */}
                <div className="apTabs">
                    {tabs.map((tab) => (
                        <button
                            key={tab}
                            type="button"
                            onClick={() => openTab(tab)}
                            className={`apTab${activeTab === tab ? " on" : ""}${tab === "Guides" ? " guides" : ""}`}
                        >
                            <span>{tab}</span>
                            {tab === "DEX Walletless Api" && !walletlessSeen && (
                                <span className="apBdg updated">UPDATED</span>
                            )}
                            {tab === "Guides" && (
                                <span className="apBdg new">NEW</span>
                            )}
                        </button>
                    ))}
                </div>

                {/* Copy-for-AI: the ACTIVE tab's full spec (partner ids already
                    injected into the examples) wrapped in an integration prompt,
                    one paste into any AI agent. */}
                {activeTab !== "Changelog" && (() => {
                    const aiSpec = activeTab === "DEX Walletless Api" || activeTab === "Guides" ? walletlessSpec
                        : activeTab === "Trade API" ? tradeSpec
                        : activeTab === "Partners cms api" ? partnerCmsSpec
                        : null;
                    // The Guides tab can copy before the walletless spec was ever opened: load it on demand.
                    const canCopy = Boolean(aiSpec) || activeTab === "Guides";
                    const copyForAi = async () => {
                        if (!canCopy) return;
                        try {
                            let specObj = aiSpec;
                            if (!specObj && activeTab === "Guides") {
                                specObj = await loadWalletlessSpec();
                                setWalletlessSpec(specObj);
                            }
                            const specYaml = yaml.dump(specObj, { lineWidth: 120, noRefs: true });
                            // The walletless guides (webhooks, deposits, limits, rails...) travel with the spec.
                            let guides = "";
                            if (activeTab === "DEX Walletless Api" || activeTab === "Guides") {
                                try { guides = typeof docs.Guides === "string" ? docs.Guides : await fetchDoc("Guides"); } catch (_) { guides = ""; }
                            }
                            // Walletless: get the agent live fast (the proven 1-minute path),
                            // but make it aware of the privacy rails and the other products
                            // so it offers them when the use case fits. No mandatory intake.
                            const walletlessBrief = [
                                "You are helping me integrate the Instaswap DEX Walletless Api (no-KYC, walletless: my users just send a deposit, no wallet signing) into my application.",
                                "Below is the COMPLETE OpenAPI specification, with my partner identifiers already filled into the examples. Read it fully.",
                                "",
                                "DEFAULT PATH, get me live fast: implement POST /quote-lite -> POST /swap-lite -> status (webhooks with HMAC X-Instaswap-Signature preferred, GET /swap/{id}/status-lite polling as fallback, it is rate limited to 1,000 req/hour), with proper error handling and refund/failed states. Ask about my stack only if you cannot infer it, then produce working code.",
                                "",
                                "KNOW THE FULL SURFACE and offer it when it fits what I describe, without slowing me down:",
                                "- Privacy on swaps: add private: true for the confidential rail (fast, ~minutes, auto-fallback to zkproof) or private: \"force\" for the zkproof rail (strongest privacy, adds ~1 hour). The response shape is identical to a public swap.",
                                "- Split Swaps (POST /split/quote-lite, POST /split/swap-lite, GET /split/{id}/status-lite): one deposit paid out to 1..N wallets, each optionally a different asset/network, always untraceable by design. rail: confidential (default) | zkproof | public (traceable fallback, only when availableRails allows). distribution: even | random | custom (per-recipient share). Paying several wallets = ONE Split Swap, not several swaps.",
                                "- Payments: POST /payment-link-lite creates a shareable request for a fixed amount of a fixed asset (privacy: true|false), GET /payment-link-lite/{id} reads it; a link is executed through the standard endpoints with product: \"payment\" (1 recipient -> quote-lite + swap-lite, 2+ -> split endpoints). POST /payment-lite pays directly without a link (rail override public | confidential | zkproof). The payer can pay with ANY supported token.",
                                "- ENS: every address field (recipientAddress, refundAddress, recipients[].address) accepts an ENS name (alice.eth, alice.base.eth, alice.eth@base). Use GET /ens/resolve-lite to preview and pin the address via expectedAddress, handle 409 ens_changed and the ens_* error codes, never cache a resolved address beyond one order.",
                                "- Always quote before creating. Respect amount_below_minimum / amount_above_maximum (structured, per leg) and confidential_rail_unavailable with availableRails. Asset ids come from GET /tokens-lite and GET /pairs-lite.",
                                "- Optional X-Sub-Affiliate-Id header attributes traffic to my downstream integrators; feeBreakdown exposes the instaswap / affiliate / subAffiliate tiers.",
                            ];
                            const genericBrief = [
                                `You are helping me integrate the Instaswap ${activeTab} into my application.`,
                                "Below is the COMPLETE OpenAPI specification for this API, with my partner identifiers already filled into the examples.",
                                "Read it fully, then help me implement the integration end to end: authentication, the core flows, status handling, webhooks where available and error handling. Ask about my stack only if you cannot infer it, then produce working code.",
                            ];
                            const text = [
                                ...(activeTab === "DEX Walletless Api" || activeTab === "Guides" ? walletlessBrief : genericBrief),
                                "",
                                "----- OPENAPI SPECIFICATION (YAML) -----",
                                specYaml,
                                ...(guides ? ["", "----- INTEGRATION GUIDES (webhooks, deposits, lifecycle, error codes, rate limits, privacy rails, payments, ENS) -----", guides] : []),
                            ].join("\n");
                            await navigator.clipboard.writeText(text);
                            setAiCopied(true);
                            setTimeout(() => setAiCopied(false), 2500);
                        } catch (_) { /* clipboard unavailable */ }
                    };
                    const aiAccent = "#23dc85";
                    return (
                        <div style={{ display: "flex", justifyContent: activeTab === "Guides" ? "space-between" : "flex-end", alignItems: "center", gap: 12, flexWrap: "wrap", margin: "12px 0 0" }}>
                            {activeTab === "Guides" && (
                                <input
                                    type="search"
                                    className="apDocsSearch"
                                    placeholder="Search the guides: webhook, depositMemo, 429, ENS, zkproof…"
                                    value={guidesQuery}
                                    onChange={(e) => setGuidesQuery(e.target.value)}
                                    aria-label="Search the guides"
                                />
                            )}
                            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginLeft: "auto" }}>
                            <span style={{ fontSize: 12.5, color: "#a7b2ab" }}>
                                Integrate in minutes: paste this into ChatGPT, Claude, Cursor or any AI agent.
                            </span>
                            <button
                                type="button"
                                onClick={copyForAi}
                                disabled={!canCopy}
                                title="Copies the full OpenAPI spec and the guides with an integration prompt. Paste it into ChatGPT, Claude or any AI agent."
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 9,
                                    padding: "11px 20px",
                                    borderRadius: 11,
                                    border: `1px solid ${aiAccent}`,
                                    background: aiCopied ? aiAccent : `linear-gradient(135deg, ${aiAccent}33, ${aiAccent}14)`,
                                    color: aiCopied ? "#0d1512" : aiAccent,
                                    boxShadow: canCopy ? `0 0 22px ${aiAccent}40` : "none",
                                    fontSize: 14,
                                    fontWeight: 800,
                                    letterSpacing: ".01em",
                                    cursor: canCopy ? "pointer" : "not-allowed",
                                    opacity: canCopy ? 1 : 0.5,
                                    transition: "all .25s",
                                }}
                            >
                                <span aria-hidden="true" style={{ fontSize: 16 }}>{aiCopied ? "\u2713" : "\u2728"}</span>
                                {aiCopied ? "Copied. Paste it to your AI agent" : "Copy docs for AI agent"}
                            </button>
                            </div>
                        </div>
                    );
                })()}

                {/* Tab Content */}
                {DOCS[activeTab] && (
                    typeof docs[activeTab] === "object" && docs[activeTab]?.error
                        ? <p className="text-white text-xl text-center text-red-500">{docs[activeTab].error}</p>
                        : <DocsMarkdown markdown={typeof docs[activeTab] === "string" ? docs[activeTab] : null} toc={activeTab === "Guides"} query={activeTab === "Guides" ? guidesQuery : ""} />
                )}

                {activeTab === "DEX Walletless Api" && (
                    <div className="swagger-wrapper">
                        <SwaggerUI
                            spec={walletlessSpec}
                            docExpansion={isMobile ? "none" : "list"}
                            defaultModelsExpandDepth={isMobile ? -1 : 1}
                            defaultModelExpandDepth={isMobile ? 0 : 1}
                            requestInterceptor={(request) => {
                                try {
                                    const affiliateId = localStorage.getItem("affiliateId");
                                    if (affiliateId) {
                                        // Fill a missing affiliateId AND replace the baked example
                                        // id (FTQMPLHK = inactive test partner 14): Try-it-out
                                        // keeps the example in the body, so only-if-missing left
                                        // every request running as the dead partner (owner 2026-08-10).
                                        if (request.body && typeof request.body === "string") {
                                            const parsed = JSON.parse(request.body);
                                            if (parsed && typeof parsed === "object" && (!parsed.affiliateId || parsed.affiliateId === "FTQMPLHK")) {
                                                parsed.affiliateId = affiliateId;
                                                request.body = JSON.stringify(parsed);
                                            }
                                        }
                                        // Same for GET endpoints carrying it in the query string.
                                        // Query-only: a path id could theoretically contain the
                                        // placeholder as a substring.
                                        const qs = request.url ? request.url.indexOf("?") : -1;
                                        if (qs >= 0 && request.url.includes("FTQMPLHK", qs)) {
                                            request.url = request.url.slice(0, qs) + request.url.slice(qs).replaceAll("FTQMPLHK", encodeURIComponent(affiliateId));
                                        }
                                        // Optionally add as header for traceability
                                        request.headers = request.headers || {};
                                        request.headers["X-Instaswap-Affiliate"] = affiliateId;
                                    }
                                } catch (e) {
                                    // noop if body isn't JSON
                                }
                                return request;
                            }}
                        />
                        {walletlessErr && (
                            <p className="text-white text-xl text-center text-red-500">{walletlessErr}</p>
                        )}
                    </div>
                )}

                {activeTab === "Trade API" && (
                    <div className="swagger-wrapper">
                        {/* Mount SwaggerUI only once the spec is loaded: swagger-ui-react
                            does not reliably re-render when `spec` goes null -> object
                            after the async fetch (same reason the Partners CMS tab guards). */}
                        {tradeSpec ? (
                            <SwaggerUI
                                spec={tradeSpec}
                                docExpansion={isMobile ? "none" : "list"}
                                defaultModelsExpandDepth={isMobile ? -1 : 1}
                                defaultModelExpandDepth={isMobile ? 0 : 1}
                                requestInterceptor={(request) => {
                                    try {
                                        const affiliateId = localStorage.getItem("affiliateId");
                                        if (affiliateId) {
                                            // Same placeholder replacement as the walletless tab.
                                            if (request.body && typeof request.body === "string") {
                                                const parsed = JSON.parse(request.body);
                                                if (parsed && typeof parsed === "object" && (!parsed.affiliateId || parsed.affiliateId === "FTQMPLHK")) {
                                                    parsed.affiliateId = affiliateId;
                                                    request.body = JSON.stringify(parsed);
                                                }
                                            }
                                            const qsT = request.url ? request.url.indexOf("?") : -1;
                                            if (qsT >= 0 && request.url.includes("FTQMPLHK", qsT)) {
                                                request.url = request.url.slice(0, qsT) + request.url.slice(qsT).replaceAll("FTQMPLHK", encodeURIComponent(affiliateId));
                                            }
                                            request.headers = request.headers || {};
                                            request.headers["X-Instaswap-Affiliate"] = affiliateId;
                                        }
                                    } catch (e) {
                                        // noop if body isn't JSON
                                    }
                                    return request;
                                }}
                            />
                        ) : !tradeErr ? (
                            <div className="gtScroll" style={{ marginTop: 8 }}>
                                <div className="gtEmpty" style={{ padding: "70px 20px" }}>
                                    <div style={{ fontSize: 13 }}>Loading Trade API reference…</div>
                                </div>
                            </div>
                        ) : null}
                        {tradeErr && (
                            <p className="text-white text-xl text-center text-red-500">{tradeErr}</p>
                        )}
                    </div>
                )}

                {activeTab === "Partners cms api" && (
                    <div className="swagger-wrapper">
                        <PartnerApiKeyPanel />
                        {partnerCmsSpec && (
                            <SwaggerUI
                                spec={partnerCmsSpec}
                                docExpansion={isMobile ? "none" : "list"}
                                defaultModelsExpandDepth={isMobile ? -1 : 1}
                                defaultModelExpandDepth={isMobile ? 0 : 1}
                            />
                        )}
                        {partnerCmsErr && (
                            <p className="text-white text-xl text-center text-red-500">{partnerCmsErr}</p>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default ApiReference;
